// @flow

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {Divider, Icon, Grid, makeStyles, Typography} from '@material-ui/core';
import {Edit, GetApp} from '@material-ui/icons';

import LoadingSpinner from '../../../../../../elements/LoadingSpinner/LoadingSpinner';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import UploadImageButton from '../../../../../../elements/UploadImageButton/UploadImageButton';
import SnackBar from '../../../../../../elements/SnackBar/SnackBar';
import {useActions} from '../../../../../../utils/globalStore';
import {actions as ProductPageActions} from '../../../duck';

import {colors} from '../../../../../../assets/styles/colors';
import {createFile} from '../../../../../../utils/image';
import {getImageUrl} from '../../../../../../utils/image_provider';

import CropModal from '../../../../../../elements/CropModal';

import type {ImageToCrop} from '../../../../../../elements/CropModal';
import type {SizeChart} from '../../../duck';
import type {RootState} from '../../../../..';
import type {Theme} from '@material-ui/core';
import {useMap} from '../../../../../../utils/react/useMap';

type Props = {
  trigger: (name: string) => Promise<boolean>,
  chartToEdit?: SizeChart,
  // setStep: number => void,
  getValues: (field: string) => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  title: {
    color: colors.grey900,
    fontSize: 16,
    fontWeight: 600,
  },
  subtitle: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
  },
  checkboxList: {
    width: '100%',
    margin: theme.spacing(2),
  },
  checkboxLabel: {
    fontSize: 14,
    fontWeight: 400,
    color: colors.grey900,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    // [theme.breakpoints.up('md')]: {
    //   justifyContent: 'space-between',
    // },
  },
  actionButton: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '15rem',
    },
  },
  deleteImage: {
    borderRadius: 8,
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
    backgroundColor: colors.grey300,
  },
  bgLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: '0.5',
    pointerEvents: 'none',
    minHeight: '328px',
  },
}));

const ASPECT_RATIO = 2.33; //1400 x 600
const TOTAL_IMAGES = 2;
const INITIAL_IMAGE_COLLECTION = new Array(TOTAL_IMAGES)
  .fill(null)
  .reduce((acc, _, idx) => {
    acc[idx] = null;
    return acc;
  }, {});
export default function UploadImage(props: Props) {
  const {chartToEdit, getValues, trigger} = props;
  const classes = useStyles();

  const saveSizeChart = useActions(ProductPageActions.saveSizeChart);
  const editSizeChart = useActions(ProductPageActions.editSizeChart);

  const isSavingSizeChart = useSelector(
    (state: RootState) => state.productPage.isSavingSizeChart
  );
  const saveSizeChartError = useSelector(
    (state: RootState) => state.productPage.saveSizeChartError
  );

  const [imageToCrop, setImageToCrop] = useState<ImageToCrop | null>(null);
  const guideSizeImageMap = useMap<number, ImageToCrop | null>(
    INITIAL_IMAGE_COLLECTION
  );

  const currentImageIndexRef = useRef<number>(0);

  const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);

  const [isMissingImages, setIsMissingImages] = useState<boolean>(false);

  useEffect(() => {
    if (!chartToEdit) return;

    const {image1Id, image2Id} = chartToEdit;
    if (image1Id) {
      const imageUrl = getImageUrl(image1Id);
      createFile(imageUrl)
        .then(({file}) => {
          const img = URL.createObjectURL(file);
          guideSizeImageMap.set(0, {preview: img, file});
        })
        .finally(() => setIsLoadingImages(false));
    }
    if (image2Id) {
      const imageUrl = getImageUrl(image2Id);
      createFile(imageUrl)
        .then(({file}) => {
          const img = URL.createObjectURL(file);
          guideSizeImageMap.set(1, {preview: img, file});
        })
        .finally(() => setIsLoadingImages(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartToEdit]);

  const handleChangeImage = useCallback(
    async (event: SyntheticInputEvent<HTMLInputElement>, idx: number) => {
      const file = event.target.files[0];
      if (!file) return;

      currentImageIndexRef.current = idx;
      setImageToCrop({
        preview: URL.createObjectURL(file),
        file,
      });
    },
    []
  );

  const handleRemoveImage = useCallback(
    (idx: number) => {
      guideSizeImageMap.set(idx, null);
      // setSavedImages(prev => fillArray([...prev], idx, ''));
    },
    [guideSizeImageMap]
  );

  const handleContinueStep = () => {
    const resultImagesCropped = guideSizeImageMap.values();

    const completedImages = resultImagesCropped.some(image => !!image);

    trigger('sizeGuideName').then(filled => {
      if (!filled) return;

      if (completedImages) {
        const name = getValues('sizeGuideName');
        const imagesFiles = resultImagesCropped.map(result => result?.file);
        if (chartToEdit) {
          editSizeChart({
            sizeChartRequest: {
              id: chartToEdit.id,
              name,
              files: imagesFiles,
              sizeChartType: 'IMAGE',
            },
          });
        } else {
          saveSizeChart({
            sizeChartRequest: {
              name,
              files: imagesFiles,
              sizeChartType: 'IMAGE',
            },
          });
        }
      } else setIsMissingImages(true);
    });
  };

  const guideSizeImagePreview = useMemo(() => {
    return guideSizeImageMap
      .values()
      .map((result, index) => (result ? result.preview : ''));
  }, [guideSizeImageMap]);
  return (
    <>
      {isMissingImages && (
        <SnackBar
          mode="info"
          message="Se requirere 1 imagen como mínimo."
          handleClose={() => setIsMissingImages(false)}
        />
      )}
      {saveSizeChartError && !chartToEdit && (
        <SnackBar mode="error" message="No se pudo guardar la guía de tallas" />
      )}

      {isSavingSizeChart && <LoadingSpinner />}

      <div className={classes.root}>
        <div>
          {imageToCrop && (
            <CropModal
              open
              aspect={ASPECT_RATIO}
              onClose={() => setImageToCrop(null)}
              images={[imageToCrop]}
              onSubmit={result => {
                const currentImageIndex = currentImageIndexRef.current;
                guideSizeImageMap.set(currentImageIndex, result[0]);

                setImageToCrop(null);
              }}
              compressOptions={{
                quality: 0.5,
                maxWidth: 3840,
                maxHeight: 3840 / ASPECT_RATIO,
              }}
              keepFormat
            />
          )}

          <Typography className={classes.title}>
            Sube un máximo de 2 fotos
          </Typography>

          <div
            style={{
              margin: '16px 0',
              opacity: isLoadingImages ? '0.3' : 1,
              pointerEvents: isLoadingImages ? 'none' : undefined,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <UploadImageButton
                  position={0}
                  image={guideSizeImagePreview[0]}
                  onImageDelete={handleRemoveImage}
                  onImageChange={handleChangeImage}
                  action={({handleDelete}) => (
                    <CustomButton
                      className={classes.deleteImage}
                      startIcon={<Edit />}
                      color="#3A3A49"
                      onClick={handleDelete}
                    >
                      Cambiar Imagen
                    </CustomButton>
                  )}
                >
                  <div>
                    <Icon>
                      <GetApp />
                    </Icon>
                    <Typography color="primary">
                      Sube foto de guía <br />
                      (1400 x 600)
                    </Typography>
                  </div>
                </UploadImageButton>
              </Grid>
              {guideSizeImagePreview.length > 0 && (
                <Grid item xs={10}>
                  <UploadImageButton
                    position={1}
                    image={guideSizeImagePreview[1]}
                    onImageDelete={handleRemoveImage}
                    onImageChange={handleChangeImage}
                    action={({handleDelete}) => (
                      <CustomButton
                        className={classes.deleteImage}
                        startIcon={<Edit />}
                        color="#3A3A49"
                        onClick={handleDelete}
                      >
                        Cambiar Imagen
                      </CustomButton>
                    )}
                  >
                    <div>
                      <Icon>
                        <GetApp />
                      </Icon>
                      <Typography color="primary">
                        Sube foto de guía <br />
                        (1400 x 600)
                      </Typography>
                    </div>
                  </UploadImageButton>
                </Grid>
              )}
            </Grid>
          </div>
        </div>
      </div>

      <Divider className={classes.divider} style={{margin: '48px 0 16px 0'}} />
      <div
        className={classes.actionButtons}
        // style={{justifyContent: step ? 'space-between' : 'center'}}
      >
        <CustomButton
          // disabled={!imagesCompleted || !getValues('sizeGuideName')}
          className={classes.actionButton}
          variant="contained"
          onClick={handleContinueStep}
        >
          Guardar Guía
          {/* {imagesCompleted ? 'Guardar Guía' : 'Siguiente'} */}
        </CustomButton>
      </div>
    </>
  );
}

UploadImage.propTypes = {
  trigger: PropTypes.func.isRequired,
  chartToEdit: PropTypes.object,
  // setStep: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
};
